/**
 * TS-MAX: Maximum Mennyiség Korlátozás
 *
 * Ha egy terméknél be van állítva a custom.maximum_qty metafield,
 * akkor nem lehet több darabot belerakni a kosárba, mint amennyi ebben a metafield-ben van.
 */

(function() {
  'use strict';

  // Kosár lekérése
  async function getCart() {
    try {
      const response = await fetch(window.routes.cart_url + '.js');
      if (!response.ok) throw new Error('Cart fetch failed');
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  // Termék adatok lekérése (metafield-ekkel együtt)
  async function getProductData(productId, productHandle = null) {
    try {
      let handle = productHandle;

      // Ha nincs handle, megkeressük a kosárból vagy a DOM-ból
      if (!handle) {
        const productForm = document.querySelector(`product-form[data-product-id="${productId}"]`);
        if (productForm && productForm.dataset.productHandle) {
          handle = productForm.dataset.productHandle;
        } else {
          // Próbáljuk meg a termék oldalról
          const cart = await getCart();
          if (cart) {
            const cartItem = cart.items.find(item => item.product_id === productId);
            if (cartItem && cartItem.handle) {
              handle = cartItem.handle;
            }
          }
        }
      }

      if (!handle) {
        return null;
      }

      // A termék handle-ját használjuk a JSON API-hoz
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error('Product fetch failed');
      }
      const productData = await response.json();

      return productData;
    } catch (error) {
      return null;
    }
  }

  function getMaximumQuantity(product) {
    if (!product) {
      return null;
    }

    let metafields = null;

    if (product.metafields && Array.isArray(product.metafields)) {
      metafields = product.metafields;
    } else if (product.metafields_global) {
      metafields = Object.values(product.metafields_global).flat();
    } else if (product.metafields && typeof product.metafields === 'object') {
      metafields = Object.values(product.metafields);
    } else if (product.metafields && product.metafields.custom) {
      const customMetafields = product.metafields.custom;
      if (typeof customMetafields === 'object') {
        metafields = Object.values(customMetafields);
      }
    }

    if (!metafields || !Array.isArray(metafields)) {
      if (product.metafields && product.metafields.custom && product.metafields.custom.maximum_qty) {
        const value = product.metafields.custom.maximum_qty;
        const maxQty = parseInt(value, 10);
        if (!isNaN(maxQty) && maxQty > 0) {
          return maxQty;
        }
      }
      return null;
    }

    const maxQtyMetafield = metafields.find(m => {
      if (!m) return false;
      const keyMatch = m.key === 'maximum_qty' || m.key === 'custom.maximum_qty' || m.key === 'maximum_qty';
      const namespaceMatch = m.namespace === 'custom' || !m.namespace;
      return keyMatch && namespaceMatch;
    });

    if (!maxQtyMetafield) {
      return null;
    }

    const value = maxQtyMetafield.value;
    const maxQty = parseInt(value, 10);

    if (isNaN(maxQty) || maxQty <= 0) {
      return null;
    }

    return maxQty;
  }

  function getMaximumQuantityFromInput(productForm) {
    if (!productForm) {
      return null;
    }

    const form = productForm.querySelector('form');
    if (!form) {
      return null;
    }

    const maxQtyInput = form.querySelector('input[name="maximum_qty"], input[data-maximum-qty]');
    if (!maxQtyInput) {
      return null;
    }

    const value = maxQtyInput.value;
    const maxQty = parseInt(value, 10);

    if (isNaN(maxQty) || maxQty <= 0) {
      return null;
    }

    return maxQty;
  }

  async function getCartQuantityForProduct(productId, variantId) {
    const cart = await getCart();
    if (!cart || !cart.items) {
      return 0;
    }

    let totalQuantity = 0;
    const normalizedProductId = normalizeProductId(productId);
    const normalizedVariantId = parseInt(variantId, 10);

    for (const item of cart.items) {
      const itemProductId = normalizeProductId(item.product_id);
      const itemVariantId = parseInt(item.variant_id, 10);

      if (itemProductId === normalizedProductId || itemVariantId === normalizedVariantId) {
        totalQuantity += item.quantity;
      }
    }

    return totalQuantity;
  }

  function normalizeProductId(productId) {
    if (!productId) {
      return null;
    }

    const gidMatch = String(productId).match(/Product\/(\d+)/);
    if (gidMatch) {
      return parseInt(gidMatch[1], 10);
    }

    const numId = parseInt(productId, 10);
    if (!isNaN(numId)) {
      return numId;
    }

    return productId;
  }

  async function checkMaximumQuantity(productForm, variantId, quantityToAdd) {
    const productId = productForm.dataset.productId;
    if (!productId) {
      return { valid: true };
    }

    let maxQty = getMaximumQuantityFromInput(productForm);

    if (!maxQty) {
      const productHandle = productForm.dataset.productHandle || null;
      const productData = await getProductData(productId, productHandle);
      maxQty = getMaximumQuantity(productData);
    }

    if (!maxQty) {
      return { valid: true };
    }

    const normalizedProductId = normalizeProductId(productId);
    const cartQuantity = await getCartQuantityForProduct(normalizedProductId, variantId);
    const newTotalQuantity = cartQuantity + quantityToAdd;

    if (newTotalQuantity > maxQty) {
      return {
        valid: false,
        maxQty: maxQty,
        currentQty: cartQuantity,
        requestedQty: quantityToAdd
      };
    }

    return { valid: true };
  }

  const checkingForms = new WeakSet();
  const allowedSubmits = new WeakSet();

  function interceptFormSubmit() {
    document.addEventListener('submit', async function(event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') {
        return;
      }

      const productForm = form.closest('product-form');
      if (!productForm) {
        return;
      }

      if (allowedSubmits.has(form)) {
        allowedSubmits.delete(form);
        return;
      }

      if (checkingForms.has(form)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const formData = new FormData(form);
      const variantId = formData.get('id');
      const quantity = parseInt(formData.get('quantity') || '1', 10);

      if (variantId && !isNaN(quantity) && quantity > 0) {
        checkingForms.add(form);

        const result = await checkMaximumQuantity(productForm, variantId, quantity);

        checkingForms.delete(form);

        if (!result.valid) {
          const message = `Többet már nem lehet belerakni a kosárba a termékek közül. Maximum mennyiség: ${result.maxQty} darab.`;

          const errorMessageWrapper = productForm.querySelector('.product-form__error-message-wrapper');
          const errorMessage = errorMessageWrapper?.querySelector('.product-form__error-message');

          if (errorMessageWrapper && errorMessage) {
            errorMessage.textContent = message;
            errorMessageWrapper.removeAttribute('hidden');

            setTimeout(() => {
              errorMessageWrapper.setAttribute('hidden', '');
              errorMessage.textContent = '';
            }, 5000);
          } else {
            alert(message);
          }

          const submitButton = productForm.querySelector('[type="submit"]');
          if (submitButton) {
            submitButton.removeAttribute('aria-disabled');
            submitButton.classList.remove('loading');
          }

          return;
        } else {
          allowedSubmits.add(form);

          setTimeout(() => {
            const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
            form.dispatchEvent(submitEvent);
          }, 10);
        }
      } else {
        allowedSubmits.add(form);
        setTimeout(() => {
          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(submitEvent);
        }, 10);
      }
    }, true);
  }

  async function getMaximumQuantityForCartItem(cartItem) {
    let line = null;

    const idMatch = cartItem.id?.match(/(?:CartItem|CartDrawer-Item)-(\d+)/);
    if (idMatch) {
      line = parseInt(idMatch[1], 10);
    } else {
      line = cartItem.dataset.index ? parseInt(cartItem.dataset.index, 10) : null;
    }

    if (!line) {
      return null;
    }

    const cart = await getCart();
    if (!cart || !cart.items) {
      return null;
    }

    const item = cart.items[line - 1];
    if (!item) {
      return null;
    }

    const normalizedProductId = normalizeProductId(item.product_id);
    const productHandle = item.handle;

    const productData = await getProductData(normalizedProductId, productHandle);
    const maxQty = getMaximumQuantity(productData);

    if (!maxQty) {
      try {
        const productPageResponse = await fetch(`/products/${productHandle}`);
        if (productPageResponse.ok) {
          const productPageHTML = await productPageResponse.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(productPageHTML, 'text/html');

          const maxQtyInput = doc.querySelector('input[name="maximum_qty"], input[data-maximum-qty]');
          if (maxQtyInput && maxQtyInput.value) {
            const value = parseInt(maxQtyInput.value, 10);
            if (!isNaN(value) && value > 0) {
              return value;
            }
          }
        }
      } catch (error) {
        // Silent fail
      }
    }

    return maxQty;
  }

  function interceptCartQuantityChange() {
    document.addEventListener('click', async function(event) {
      const button = event.target.closest('button[name="plus"]');
      if (!button) {
        return;
      }

      const quantityInput = button.closest('quantity-input');
      if (!quantityInput) {
        return;
      }

      const cartItem = quantityInput.closest('.cart-item') || quantityInput.closest('[id^="CartItem-"]') || quantityInput.closest('[id^="CartDrawer-Item-"]');
      if (!cartItem) {
        return;
      }

      const input = quantityInput.querySelector('input');
      if (!input) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const currentQuantity = parseInt(input.value, 10);
      const newQuantity = currentQuantity + 1;

      const maxQty = await getMaximumQuantityForCartItem(cartItem);

      if (maxQty && newQuantity > maxQty) {
        const message = `Többet már nem lehet belerakni a kosárba a termékek közül. Maximum mennyiség: ${maxQty} darab.`;

        const line = parseInt(cartItem.id?.match(/(?:CartItem|CartDrawer-Item)-(\d+)/)?.[1] || cartItem.dataset.index || '0', 10);
        if (line > 0) {
          const lineItemError = document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
          if (lineItemError) {
            const errorText = lineItemError.querySelector('.cart-item__error-text');
            if (errorText) {
              errorText.textContent = message;
              lineItemError.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

              setTimeout(() => {
                errorText.textContent = '';
              }, 5000);
            } else {
              alert(message);
            }
          } else {
            alert(message);
          }
        } else {
          alert(message);
        }

        input.value = currentQuantity;
        return false;
      }

      input.value = newQuantity;
      input.dispatchEvent(new Event('change', { bubbles: true }));

      return false;
    }, true);
  }

  function init() {
    interceptFormSubmit();
    interceptCartQuantityChange();
  }

  // DOM ready esemény
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Exportálás globális eléréshez
  window.TS_MAX = {
    init: init,
    checkMaximumQuantity: checkMaximumQuantity
  };
})();

